import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { MainLayout } from "./layouts/MainLayout";
import { ToastProvider } from "./components/Toast";
import { ConfirmProvider } from "./components/ConfirmDialog";

import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ForgotUsernamePage from "./pages/auth/ForgotUsernamePage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import MemberSearchPage from "./pages/eligibility/MemberSearchPage";
import ProviderRosterPage from "./pages/providerroster/ProviderRosterPage";
import InvoiceListPage from "./pages/invoices/InvoiceListPage";
import PaymentHistoryPage from "./pages/invoices/PaymentHistoryPage";
import AuthorizationRequestPage from "./pages/authorizations/AuthorizationRequestPage";
import AuthorizationInquiryPage from "./pages/authorizations/AuthorizationInquiryPage";
import ClaimStatusPage from "./pages/claims/ClaimStatusPage";
import ClaimDetailPage from "./pages/claims/ClaimDetailPage";
import AppealPage from "./pages/claims/AppealPage";
import MyAppealsPage from "./pages/claims/MyAppealsPage";
import EOPSearchPage from "./pages/claims/EOPSearchPage";
import FormsPage from "./pages/tools/FormsPage";
import EnrollmentPage from "./pages/forms/EnrollmentPage";
import ContactResourcesPage from "./pages/tools/ContactResourcesPage";
import ContactResourcesPageInner from "./pages/tools/ContactResourcesPageInner";
import UserManagementPage from "./pages/admin/UserManagementPage";
import SystemUsersPage from "./pages/admin/SystemUsersPage";
import SystemSettingsPage from "./pages/admin/SystemSettingsPage";
import FeedbackAdminPage from "./pages/admin/FeedbackAdminPage";
import ProfilePage from "./pages/profile/ProfilePage";
import TermsOfServicePage from "./pages/legal/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import ContactUsPage from "./pages/legal/ContactUsPage";

import EFTEnrollmentPage from "./pages/forms/EFTEnrollmentPage";
import ERAEnrollmentPage from "./pages/forms/ERAEnrollmentPage";
import EFTEnrollmentListPage from "./pages/forms/EFTEnrollmentListPage";
import ERAEnrollmentListPage from "./pages/forms/ERAEnrollmentListPage";
import ClaimAppealListPage from "./pages/claims/ClaimAppealListPage";

import NotificationsPage from "./pages/notifications/NotificationsPage";
import HelpPage from "./pages/help/HelpPage";

import { NotificationProvider } from "./contexts/NotificationProvider";

function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <NotificationProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/forgot-username" element={<ForgotUsernamePage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/contact" element={<ContactResourcesPage />} />

              {/* Protected routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* Available to all logged-in users */}
                <Route path="/" element={<DashboardPage />} />
                <Route
                  path="/eligibility"
                  element={
                    <ProtectedRoute>
                      <MemberSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proster"
                  element={
                    <ProtectedRoute>
                      <ProviderRosterPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/invoices" element={<InvoiceListPage />} />
                <Route
                  path="/invoices/payments"
                  element={<PaymentHistoryPage />}
                />
                <Route
                  path="/authorizations"
                  element={<AuthorizationRequestPage />}
                />
                <Route
                  path="/authorizations/inquiry"
                  element={<AuthorizationInquiryPage />}
                />
                <Route
                  path="/claims"
                  element={
                    <ProtectedRoute>
                      <ClaimStatusPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/appeals" element={<ClaimAppealListPage />} />
                <Route
                  path="/claims/:id/appeal"
                  element={
                    <ProtectedRoute>
                      <AppealPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/claims/:id/appeal/:aid/:uid"
                  element={
                    <ProtectedRoute>
                      <AppealPage />
                    </ProtectedRoute>
                  }
                />
                {/*<Route path="/appeals" element={<MyAppealsPage />} />*/}
                <Route
                  path="/claims/:claimNo/:chqno/:billingPro"
                  element={
                    <ProtectedRoute>
                      <ClaimDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/eop"
                  /*element={<EOPSearchPage />}*/
                  element={
                    <ProtectedRoute>
                      <EOPSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/forms" element={<FormsPage />} />

                {/* Provider Admin + Super Admin */}
                <Route
                  path="/forms/eft-enrollment_original"
                  element={
                    <ProtectedRoute
                      allowedRoles={["Provider Admin", "Super Admin"]}
                    >
                      <EnrollmentPage kind="eft" />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/era-enrollment"
                  element={
                    <ProtectedRoute
                      page=""
                      allowedRoles={[
                        "Provider Admin",
                        // 'Super Admin'
                      ]}
                    >
                      <ERAEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/era-enrollment/:uid/:mode"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="eraEnrollments"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <ERAEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/era-enrollment/:uid/:mode/:id"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="eraEnrollments"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <ERAEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/eft-enrollment"
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        "Provider Admin",
                        // 'Super Admin'
                      ]}
                    >
                      <EFTEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/eft-enrollment/:uid/:mode"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="eftEnrollments"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <EFTEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/eft-enrollment/:uid/:mode/:id"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="eftEnrollments"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <EFTEnrollmentPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/eft-enrollment-list"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="eftEnrollments"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <EFTEnrollmentListPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/era-enrollment-list"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="eraEnrollments"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <ERAEnrollmentListPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/forms/era-enrollment_original"
                  element={
                    <ProtectedRoute
                      allowedRoles={["Provider Admin", "Super Admin"]}
                    >
                      <EnrollmentPage kind="era" />
                    </ProtectedRoute>
                  }
                />

                {/* Provider Admin only */}
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute allowedRoles={["Provider Admin"]}>
                      <UserManagementPage />
                    </ProtectedRoute>
                  }
                />

                {/* Super Admin only */}
                <Route
                  path="/admin/system-users"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="systemUsers"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <SystemUsersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/system-settings"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="systemSettings"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <SystemSettingsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Super Admin */}
                <Route
                  path="/admin/feedback"
                  element={
                    <ProtectedRoute
                      page="superAdmin"
                      section="feedbacks"
                      allowedRoles={["Super Admin", "Admin"]}
                    >
                      <FeedbackAdminPage />
                    </ProtectedRoute>
                  }
                />

                {/* Available to all logged-in users */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/contact-us" element={<ContactUsPage />} />
                <Route
                  path="/contactres"
                  element={<ContactResourcesPageInner />}
                />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </NotificationProvider>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
